import { useEffect, useRef, useState, useCallback } from 'react';
import { useUser, useHasuraName } from '../store';
import { 
  ExcelSessionClient, 
  registerLocalSession, 
  unregisterLocalSession, 
  isLocalSession,
  getOrCreatePersistedSessionId,
  broadcastSessionEvent,
  subscribeToSessionBroadcast,
} from './client';
import type { SessionBroadcastMessage } from './client';
import logger from '../logger';
import type { SessionSubscriptionData } from './types';

export interface ExistingSessionInfo {
  id: string;
  excel_session_id: string;
  last_seen: string;
}

export interface UseExcelSessionOptions {
  enableSubscription?: boolean;
  onExistingSession?: (info: ExistingSessionInfo) => Promise<boolean>;
  onSessionTakeover?: (data: SessionSubscriptionData) => void;
  onError?: (error: Error) => void;
  autoStart?: boolean;
  onLogout?: () => void;
}

export interface ExcelSessionStatus {
  isInitialized: boolean;
  isActive: boolean;
  excelSessionId: string | null;
  error: Error | null;
}

export interface ExcelSessionActions {
  startSession: () => Promise<void>;
  endSession: () => Promise<void>;
}

export function useExcelSession(options: UseExcelSessionOptions = {}) {
  const {
    enableSubscription = true,
    onExistingSession,
    onSessionTakeover,
    onError,
    autoStart = !onExistingSession, // Default to false if we need to check for existing sessions
    onLogout,
  } = options;

  const { id: userId, rawToken } = useUser();
  const hasuraName = useHasuraName();

  const [status, setStatus] = useState<ExcelSessionStatus>({
    isInitialized: false,
    isActive: false,
    excelSessionId: null,
    error: null,
  });

  const clientRef = useRef<ExcelSessionClient | null>(null);
  const excelSessionIdRef = useRef<string | null>(null);
  const isStartingRef = useRef(false);
  const isFirstSubscriptionMessageRef = useRef(true);

  const initializeClient = useCallback(async () => {
    if (!hasuraName || !rawToken || !userId) {
      logger.debug('Cannot initialize session client - missing hasuraName, token, or userId');
      return null;
    }

    if (clientRef.current) {
      logger.debug('Session client already initialized, reusing');
      return clientRef.current;
    }

    logger.info('Initializing Excel session client', { hasuraName, userId });
    
    // Get or create a persisted session ID (survives reloads)
    const sessionId = await getOrCreatePersistedSessionId(userId);
    
    const client = new ExcelSessionClient(hasuraName, rawToken, sessionId);
    clientRef.current = client;
    excelSessionIdRef.current = client.excelSessionId;
    return client;
  }, [hasuraName, rawToken, userId]);

  const startSession = useCallback(async () => {
    if (!userId || !hasuraName || !rawToken) {
      const error = new Error('Cannot start session - user not authenticated');
      logger.error('Session start failed', { userId, hasuraName, hasToken: !!rawToken });
      setStatus((prev) => ({ ...prev, error }));
      if (onError) onError(error);
      return;
    }

    if (isStartingRef.current) {
      logger.debug('Session start already in progress');
      return;
    }

    if (status.isActive && excelSessionIdRef.current) {
      logger.debug('Session already active', { excelSessionId: excelSessionIdRef.current });
      return;
    }

    // Extra guard: check if there's already a client with a running liveness probe
    if (clientRef.current) {
      logger.warn('Client exists but session not marked active', {
        existingSessionId: excelSessionIdRef.current,
        isActive: status.isActive,
        isStarting: isStartingRef.current,
      });
    }

    try {
      isStartingRef.current = true;
      logger.info('Starting Excel session', { userId });

      const client = await initializeClient();
      if (!client) {
        throw new Error('Failed to initialize session client');
      }

      logger.debug('Using excel_session_id', { excelSessionId: client.excelSessionId });

      // Register this session as running locally FIRST
      // This prevents race conditions where other add-ins see us as "different machine"
      await registerLocalSession(client.excelSessionId);

      if (onExistingSession) {
        // Small delay to allow other add-ins to register their sessions
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const existingSession = await client.checkExistingSession(userId);
        
        if (existingSession && existingSession.excel_session_id && existingSession.excel_session_id !== client.excelSessionId) {
          // Check if the existing session is running locally on this machine
          const existingIsLocal = await isLocalSession(existingSession.excel_session_id);
          
          if (existingIsLocal) {
            // Same machine - allow concurrent sessions without takeover prompt
            logger.info('Existing session is on same machine - allowing concurrent session', {
              existingSessionId: existingSession.excel_session_id,
              newSessionId: client.excelSessionId,
            });
          } else {
            // Different machine - prompt for takeover
            const shouldTakeOver = await onExistingSession({
              id: existingSession.id,
              excel_session_id: existingSession.excel_session_id,
              last_seen: existingSession.last_seen,
            });
            
            if (!shouldTakeOver) {
              logger.info('User declined to take over existing session');
              isStartingRef.current = false;
              // Unregister since we're not proceeding
              await unregisterLocalSession(client.excelSessionId);
              return;
            }
            
            logger.info('User confirmed takeover of existing session');
          }
        }
      }

      await client.updateSession({
        user_id: userId,
        excel_session_id: client.excelSessionId,
      });

      client.startLivenessProbe(userId);

      if (enableSubscription) {
        isFirstSubscriptionMessageRef.current = true;
        
        client.subscribeToSession(
          userId,
          async (data) => {
            logger.debug('Session subscription data received', { data });
            
            const currentExcelSessionId = data.session_by_pk?.excel_session_id;
            
            if (isFirstSubscriptionMessageRef.current) {
              isFirstSubscriptionMessageRef.current = false;
              return;
            }
            
            if (currentExcelSessionId && currentExcelSessionId !== excelSessionIdRef.current) {
              // Wait a moment for broadcast/registration to complete (race condition with Hasura subscription)
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Check if the new session is running locally on this machine
              const newSessionIsLocal = await isLocalSession(currentExcelSessionId);
              
              if (newSessionIsLocal) {
                // Same machine - allow concurrent sessions, don't log out
                logger.info('Session change detected but new session is on same machine - staying logged in', {
                  mySessionId: excelSessionIdRef.current,
                  newSessionId: currentExcelSessionId,
                });
                return;
              }
              
              logger.warn('Excel session takeover detected from different machine - auto-logging out', {
                mySessionId: excelSessionIdRef.current,
                newSessionId: currentExcelSessionId,
              });
              
              if (onSessionTakeover) {
                onSessionTakeover(data);
              }
              
              if (onLogout) {
                onLogout();
              } else {
                logger.warn('No onLogout callback provided - cannot auto-logout');
              }
            }
          },
          (error) => {
            logger.error('Session subscription error', { error });
            if (onError) onError(error);
          }
        );
      }

      setStatus({
        isInitialized: true,
        isActive: true,
        excelSessionId: client.excelSessionId,
        error: null,
      });

      // Broadcast to other add-ins that we've started a session
      broadcastSessionEvent({
        type: 'session_started',
        sessionId: client.excelSessionId,
        userId,
      });

      logger.info('Excel session started successfully', { excelSessionId: client.excelSessionId });
    } catch (error) {
      const err = error as Error;
      logger.error('Failed to start Excel session', { error: err, userId });
      setStatus((prev) => ({
        ...prev,
        isActive: false,
        error: err,
      }));
      if (onError) onError(err);
    } finally {
      isStartingRef.current = false;
    }
  }, [
    userId,
    hasuraName,
    rawToken,
    status.isActive,
    initializeClient,
    enableSubscription,
    onSessionTakeover,
    onError,
  ]);

  const endSession = useCallback(async () => {
    if (!userId || !clientRef.current) {
      logger.debug('No active session to end');
      return;
    }

    const sessionIdToEnd = excelSessionIdRef.current;

    try {
      logger.info('Ending Excel session', { userId, excelSessionId: sessionIdToEnd });

      // Unregister from local sessions
      if (sessionIdToEnd) {
        await unregisterLocalSession(sessionIdToEnd);
        
        // Broadcast to other add-ins that we've ended our session
        broadcastSessionEvent({
          type: 'session_ended',
          sessionId: sessionIdToEnd,
          userId,
        });
      }

      await clientRef.current.clearSession(userId);
      clientRef.current.cleanup();
      clientRef.current = null;
      excelSessionIdRef.current = null;

      setStatus({
        isInitialized: false,
        isActive: false,
        excelSessionId: null,
        error: null,
      });

      logger.info('Excel session ended successfully');
    } catch (error) {
      const err = error as Error;
      logger.error('Failed to end Excel session', { error: err, userId });
      setStatus((prev) => ({ ...prev, error: err }));
      if (onError) onError(err);
    }
  }, [userId, onError]);


  useEffect(() => {
    if (autoStart && userId && hasuraName && rawToken && !status.isActive) {
      logger.debug('Auto-starting Excel session');
      startSession();
    }
  }, [autoStart, userId, hasuraName, rawToken, status.isActive, startSession]);

  // Listen for session events from other add-ins on the same machine
  useEffect(() => {
    const handleBroadcast = async (message: SessionBroadcastMessage) => {
      switch (message.type) {
        case 'session_started':
          // Ignore our own broadcast
          if (message.sessionId === excelSessionIdRef.current) {
            logger.debug('Ignoring our own session_started broadcast');
            break;
          }
          
          // Another add-in started a session - register it as local
          // so we don't treat it as a takeover from a different machine
          logger.info('Another local add-in started a session', { 
            theirSessionId: message.sessionId,
            mySessionId: excelSessionIdRef.current,
          });
          await registerLocalSession(message.sessionId);
          
          // If we're not active AND not currently starting, but it's the same user, 
          // reinitialize our session. Skip if we're already starting to avoid disruption.
          if (!status.isActive && !isStartingRef.current && !clientRef.current && message.userId === userId && userId) {
            logger.info('Reinitializing session after another add-in logged in');
            excelSessionIdRef.current = null;
            
            // Clear error state
            setStatus({
              isInitialized: false,
              isActive: false,
              excelSessionId: null,
              error: null,
            });
            
            // Small delay to let the other add-in finish its initialization
            setTimeout(() => {
              startSession();
            }, 500);
          }
          break;
          
        case 'session_ended':
          // Another add-in ended their session
          logger.info('Another local add-in ended their session', { 
            theirSessionId: message.sessionId,
          });
          await unregisterLocalSession(message.sessionId);
          break;
          
        case 'user_logged_out':
          // Another add-in logged out - if we share the session, we should also log out
          logger.info('Another add-in logged out');
          if (onLogout) {
            onLogout();
          }
          break;
      }
    };
    
    const unsubscribe = subscribeToSessionBroadcast(handleBroadcast);
    return unsubscribe;
  }, [userId, status.isActive, startSession, onLogout]);

  useEffect(() => {
    return () => {
      if (clientRef.current && userId) {
        logger.info('Cleaning up Excel session on unmount');
        
        // Unregister from local sessions
        const sessionId = excelSessionIdRef.current;
        if (sessionId) {
          unregisterLocalSession(sessionId).catch((error) => {
            logger.error('Failed to unregister local session on unmount', { error });
          });
        }
        
        clientRef.current
          .clearSession(userId)
          .catch((error) => {
            logger.error('Failed to cleanup session on unmount', { error });
          })
          .finally(() => {
            clientRef.current?.cleanup();
          });
      }
    };
  }, [userId]);

  return {
    status,
    actions: {
      startSession,
      endSession,
    },
  };
}

